import { Facing } from "odyssey-shared";
import { CLOSING_RATE, FLY_SPEED_TILES, HOP_SPEED_TILES, LAND_GAP_TILES, MAX_STEP_MS, REST_DELAY_MS, REST_DISTANCE_TILES, START_MOVING_TILES, STOP_MOVING_TILES, TELEPORT_TILES, TRAIL_LAG_TILES, TRAIL_POINTS, TRAIL_SPACING_TILES } from "./constants.ts";

/**
 * Where a companion is drawn and how (ADR-142 decision 7.2): a pure follow over his player's drawn feet, kept by the
 * page for every player it draws a companion for and never reported to the server. It holds a ring of the points his
 * player's feet passed through, preallocated, and goes for the point `TRAIL_LAG_TILES` behind the player along that trail,
 * so he rounds the corners his player rounded; when the player stands still he goes to wait one step to the side. He
 * hops while he keeps pace, and flies while his player runs or the gap is wide, which the figure's strips (`walk`,
 * `takeoff`, `fly`, `land`) turn into the hop, the take-off, the flight and the landing.
 *
 * `step` allocates nothing: it is called once a frame for each companion drawn.
 */
export class CompanionFollow {
  /**
   * @param flyGapTiles The gap in tiles past which he takes to the air (the rules' `companion_fly_gap`).
   */
  public constructor(private readonly flyGapTiles: number) {}

  private readonly trailX = new Float32Array(TRAIL_POINTS);
  private readonly trailY = new Float32Array(TRAIL_POINTS);
  private readonly trailAt = new Float32Array(TRAIL_POINTS);
  private newest = 0;
  private stored = 0;
  private travelled = 0;
  private ownerX = Number.NaN;
  private ownerY = Number.NaN;
  private idleMs = 0;
  private flies = false;
  private moves = false;
  private pointX = 0;
  private pointY = 0;

  /** Where his feet are, in world pixels. */
  public x = 0;
  public y = 0;
  /** The way he faces. */
  public facing: number = Facing.Down;
  /** Whether he is moving this frame. */
  public moving = false;
  /** Whether he is in the air, which the sheet draws as `fly` once he moves. */
  public running = false;

  /**
   * Starts the follow beside a player, as a companion first seen: at the player's resting spot, standing.
   *
   * @param x The player's feet across, in world pixels.
   * @param y The player's feet down.
   * @param facing The way the player faces.
   * @param tile World pixels in a tile.
   */
  public reset(x: number, y: number, facing: number, tile: number): void {
    this.stored = 0;
    this.newest = 0;
    this.travelled = 0;
    this.ownerX = x;
    this.ownerY = y;
    this.idleMs = REST_DELAY_MS;
    this.flies = false;
    this.moves = false;
    this.lay(x, y);
    this.x = x + restX(facing) * REST_DISTANCE_TILES * tile;
    this.y = y + restY(facing) * REST_DISTANCE_TILES * tile;
    this.facing = facing;
    this.moving = false;
    this.running = false;
  }

  /**
   * Advances the follow one frame.
   *
   * @param ownerX The player's feet across, in world pixels, as drawn this frame.
   * @param ownerY The same down.
   * @param ownerFacing The way the player faces.
   * @param ownerRuns Whether the player is running: the view's running bit or the page's prediction.
   * @param deltaMs The frame's length in ms.
   * @param tile World pixels in a tile.
   */
  public step(ownerX: number, ownerY: number, ownerFacing: number, ownerRuns: boolean, deltaMs: number, tile: number): void {
    const dt = Math.min(deltaMs, MAX_STEP_MS) / 1000;
    const shift = Math.hypot(ownerX - this.ownerX, ownerY - this.ownerY);
    if (shift > TELEPORT_TILES * tile) {
      this.stored = 0;
      this.newest = 0;
      this.travelled = 0;
      this.lay(ownerX, ownerY);
    } else if (shift > 0) {
      this.travelled += shift;
      const last = (this.newest + TRAIL_POINTS - 1) % TRAIL_POINTS;
      if (this.travelled - (this.trailAt[last] as number) >= TRAIL_SPACING_TILES * tile) {
        this.lay(ownerX, ownerY);
      }
    }
    const ownerMoves = shift > 0;
    this.idleMs = ownerMoves ? 0 : this.idleMs + deltaMs;
    this.ownerX = ownerX;
    this.ownerY = ownerY;
    const gapToOwner = Math.hypot(ownerX - this.x, ownerY - this.y);
    this.flies = this.flies ? !((!ownerRuns || !ownerMoves) && gapToOwner < LAND_GAP_TILES * tile && !this.moves) : (ownerRuns && ownerMoves) || gapToOwner > this.flyGapTiles * tile;
    let goalX = ownerX + restX(ownerFacing) * REST_DISTANCE_TILES * tile;
    let goalY = ownerY + restY(ownerFacing) * REST_DISTANCE_TILES * tile;
    if (this.idleMs < REST_DELAY_MS && this.stored > 0) {
      this.behind(this.travelled - TRAIL_LAG_TILES * tile, ownerX, ownerY);
      goalX = this.pointX;
      goalY = this.pointY;
    }
    const dx = goalX - this.x;
    const dy = goalY - this.y;
    const away = Math.hypot(dx, dy);
    this.moves = this.moves ? away > STOP_MOVING_TILES * tile : away > START_MOVING_TILES * tile;
    if (this.moves) {
      const fastest = (this.flies ? FLY_SPEED_TILES : HOP_SPEED_TILES) * tile;
      const speed = Math.min(fastest, away * CLOSING_RATE);
      const reach = Math.min(away, speed * dt);
      this.x += (dx / away) * reach;
      this.y += (dy / away) * reach;
      this.facing = Math.abs(dx) > Math.abs(dy) * 1.2 ? (dx < 0 ? Facing.Left : Facing.Right) : Math.abs(dy) > Math.abs(dx) * 1.2 ? (dy < 0 ? Facing.Up : Facing.Down) : this.facing;
    } else {
      this.facing = ownerFacing;
    }
    this.moving = this.moves;
    this.running = this.flies;
  }

  /**
   * Lays a point on the trail at the player's feet.
   *
   * @param x Across.
   * @param y Down.
   */
  private lay(x: number, y: number): void {
    this.trailX[this.newest] = x;
    this.trailY[this.newest] = y;
    this.trailAt[this.newest] = this.travelled;
    this.newest = (this.newest + 1) % TRAIL_POINTS;
    this.stored = Math.min(TRAIL_POINTS, this.stored + 1);
  }

  /**
   * Finds the point on the trail at a distance travelled, between the two points that bracket it, the oldest point
   * when the trail does not reach back that far and the player's feet when it is asked for the present.
   *
   * @param at The distance travelled along the trail to look for.
   * @param ownerX The player's feet across, which the newest end of the trail stands for.
   * @param ownerY The same down.
   */
  private behind(at: number, ownerX: number, ownerY: number): void {
    let later = -1;
    for (let back = 0; back < this.stored; back += 1) {
      const index = (this.newest + TRAIL_POINTS - 1 - back) % TRAIL_POINTS;
      const here = this.trailAt[index] as number;
      if (here <= at) {
        const x = this.trailX[index] as number;
        const y = this.trailY[index] as number;
        const nextX = later < 0 ? ownerX : (this.trailX[later] as number);
        const nextY = later < 0 ? ownerY : (this.trailY[later] as number);
        const nextAt = later < 0 ? this.travelled : (this.trailAt[later] as number);
        const along = nextAt > here ? (at - here) / (nextAt - here) : 0;
        this.pointX = x + (nextX - x) * along;
        this.pointY = y + (nextY - y) * along;
        return;
      }
      later = index;
    }
    const oldest = (this.newest + TRAIL_POINTS - this.stored) % TRAIL_POINTS;
    this.pointX = this.trailX[oldest] as number;
    this.pointY = this.trailY[oldest] as number;
  }
}

/**
 * Which side of a player he waits on, across: beside them, never in front, so he does not stand in the way a player faces.
 *
 * @param facing The way the player faces.
 * @returns 1 for the player's right on screen when they face up or down, else 0.
 */
function restX(facing: number): number {
  return facing === Facing.Up || facing === Facing.Down ? 1 : 0;
}

/**
 * Which side of a player he waits on, down: toward the viewer when the player faces left or right.
 *
 * @param facing The way the player faces.
 * @returns 1 when the player faces left or right, else 0.
 */
function restY(facing: number): number {
  return facing === Facing.Left || facing === Facing.Right ? 1 : 0;
}
